import { useCallback, useEffect, useRef, useState } from "react";
import { Inbox, Loader2, RefreshCw, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AppShell } from "../components/AppShell";
import { NoPermission } from "../components/NoPermission";
import { PendingGroup } from "../components/bandeja/PendingGroup";
import { BandejaFilters, Pager } from "../components/bandeja/BandejaControls";
import { JobHistory, JobPanel, RunHistory } from "../components/bandeja/JobPanel";
import { AuditLogDialog } from "../components/bandeja/AuditLogDialog";
import { filesLabel, jobMsg, pollJob } from "../components/bandeja/reclassifyJob";
import { FileViewer } from "../components/client/FileViewer";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { canWrite, isAdmin, useAuth } from "../context/AuthContext";
import { api, errorMessage } from "../lib/api";
import { downloadFile } from "../lib/files";
import { notifyBandejaChanged } from "../lib/brand";

const PAGE_SIZE = 25;
const asFile = (f) => ({ id: f.id, original_filename: f.filename, content_type: f.content_type });
const folderMsg = (r) => `Movidos ${r.movidos} · siguen pendientes ${r.pendientes} · ya existían ${r.ya_existian}`;

function Bandeja() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [toDiscard, setToDiscard] = useState(null);
  const [groupToDiscard, setGroupToDiscard] = useState(null);
  const [confirmList, setConfirmList] = useState(null);
  const [page, setPage] = useState(1);
  const [reason, setReason] = useState("");
  const [query, setQuery] = useState("");
  const [q, setQ] = useState("");
  const [job, setJob] = useState(null);
  const [history, setHistory] = useState([]);
  useEffect(() => { const t = setTimeout(() => { setQ(query.trim()); setPage(1); }, 350); return () => clearTimeout(t); }, [query]);
  const load = useCallback(() => api.get("/bandeja", { params: { page, page_size: PAGE_SIZE, reason: reason || undefined, q: q || undefined } })
    .then((r) => { setData(r.data); notifyBandejaChanged(r.data.count); })
    .catch(() => toast.error("No se pudo cargar la bandeja")), [page, reason, q]);
  useEffect(() => { load(); }, [load]);
  const act = async (fn, okMsg, pendingMsg = "Procesando…") => {
    const id = toast.loading(pendingMsg);
    try { const r = await fn(); toast.success(okMsg(r), { id }); } catch (err) { toast.error(errorMessage(err, "No se pudo completar la acción, intenta de nuevo"), { id }); }
    load();
  };
  const onAssignTypeGroup = (g, client, docType) => act(async () => (await api.post("/bandeja/assign-type-group",
    { group_path: g.group_path, borrower_id: client.borrower_id, doc_type: docType })).data, folderMsg);
  const confirmDiscardGroup = () => { const g = groupToDiscard; setGroupToDiscard(null);
    act(async () => (await api.post("/bandeja/discard-group", { group_path: g.group_path })).data, (r) => `Se descartaron ${filesLabel(r.count)}`); };
  const visibleSuggestions = (data?.groups || []).filter((g) => g.near_borrower_id);
  const confirmSuggestions = () => { const list = confirmList; setConfirmList(null);
    act(async () => (await api.post("/bandeja/confirm-suggestions", { group_paths: list.map((g) => g.group_path) }, { timeout: 300000 })).data,
      (r) => `${r.carpetas} ${r.carpetas === 1 ? "carpeta confirmada" : "carpetas confirmadas"} · ${folderMsg(r)}`,
      `Asignando ${list.length} ${list.length === 1 ? "carpeta" : "carpetas"}…`); };
  const openConfirmList = () => {
    if (!visibleSuggestions.length) return toast.error("No hay sugerencias en esta página");
    setConfirmList(visibleSuggestions);
  };
  const anyDialog = !!(confirmList || toDiscard || groupToDiscard || viewing);
  // Radix can leave the body inert if a dialog's trigger unmounts while closing; never keep the page unclickable
  useEffect(() => {
    if (anyDialog) return undefined;
    const t = setTimeout(() => { document.body.style.pointerEvents = ""; }, 300);
    return () => clearTimeout(t);
  }, [anyDialog]);
  const loadRef = useRef(load);
  loadRef.current = load;
  const mounted = useRef(true);
  const [runs, setRuns] = useState([]);
  const admin = isAdmin(user);
  const loadHistory = useCallback(() => {
    api.get("/bandeja/reclassify/history").then((r) => setHistory(r.data)).catch(() => {});
    if (admin) api.get("/classify-history/runs").then((r) => setRuns(r.data)).catch(() => {});
  }, [admin]);
  const tracking = useRef(null);
  const track = useCallback(async (j) => {
    setJob(j);
    if (j.status !== "en_curso" || tracking.current === j.id) return;
    tracking.current = j.id;
    try {
      const done = await pollJob(j.id, setJob, () => !mounted.current);
      if (!done) return;
      setJob(done);
      if (done.status === "terminado") toast.success(jobMsg(done));
      else toast.error("La clasificación automática se detuvo. Intenta de nuevo");
    } catch (err) {
      toast.error(errorMessage(err, "Se perdió la conexión con el proceso de clasificación; recarga la página para ver su avance"));
    } finally {
      tracking.current = null;
    }
    loadRef.current();
    loadHistory();
  }, [loadHistory]);
  useEffect(() => {
    mounted.current = true;
    api.get("/bandeja/reclassify/current").then((r) => r.data.job && track(r.data.job)).catch(() => {});
    loadHistory();
    return () => { mounted.current = false; };
  }, [track, loadHistory]);
  const onReclassify = async () => {
    try {
      track((await api.post("/bandeja/reclassify", null, { timeout: 30000 })).data);
    } catch (err) {
      toast.error(errorMessage(err, "No se pudo volver a clasificar, intenta de nuevo"));
    }
  };
  const onDismissJob = () => api.post(`/bandeja/reclassify/${job.id}/dismiss`).then(() => { setJob(null); loadHistory(); })
    .catch((err) => toast.error(errorMessage(err, "No se pudo cerrar el resumen")));
  const running = job?.status === "en_curso";
  const onAssignFolder = (g, bid) => act(async () => (await api.post("/bandeja/assign-folder", { group_path: g.group_path, borrower_id: bid })).data, folderMsg);
  const onAssign = (f, client, docType) => act(async () => (await api.post(`/bandeja/${f.id}/assign`, { borrower_id: client.borrower_id, doc_type: docType })).data,
    (r) => (r.result === "ya_existia" ? "El archivo ya existía en ese expediente" : `Asignado a ${client.name}`));
  const onDownload = (f) => downloadFile(asFile(f), "/bandeja").catch(() => toast.error("No se pudo descargar el archivo, intenta de nuevo"));
  const confirmDiscard = () => { const f = toDiscard; setToDiscard(null); act(async () => (await api.delete(`/bandeja/${f.id}`)).data, () => "Archivo descartado"); };
  const fileHandlers = { onAssign, onView: (f) => setViewing(asFile(f)), onDownload: isAdmin(user) ? onDownload : null, onDiscard: setToDiscard };
  const pages = data ? Math.max(1, Math.ceil(data.total_groups / data.page_size)) : 1;

  return (
    <>
      <section className="tr-fade-up flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="tr-eyebrow">Carga masiva</div>
          <h1 className="text-2xl font-extrabold tracking-tight text-[#182235] sm:text-3xl" data-testid="bandeja-title">Por clasificar ({data ? data.count : "…"})</h1>
          <p className="text-sm text-[#5b667a]">Archivos del ZIP que no se pudieron asignar con seguridad. Agrupados por su carpeta original.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isAdmin(user) && (
            <button type="button" onClick={() => navigate("/ia?start=1")} className="tr-btn-primary px-4 py-2 text-sm" data-testid="bandeja-ai-button">
              <Sparkles className="h-4 w-4" /> Clasificar con IA
            </button>
          )}
          {data?.count > 0 && (
            <button type="button" onClick={onReclassify} disabled={running} className="tr-btn-light px-4 py-2 text-sm" data-testid="bandeja-reclassify-button">
              {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Volver a clasificar automáticamente
            </button>
          )}
        </div>
      </section>
      {job && (running || data?.count > 0) && <JobPanel job={job} onDismiss={onDismissJob} />}
      {admin && <AuditLogDialog />}
      {data?.count > 0 && (admin ? runs.length > 0 && <RunHistory runs={runs} /> : history.length > 0 && <JobHistory jobs={history} />)}
      {data?.count > 0 && (
        <BandejaFilters data={data} reason={reason} query={query} onQuery={setQuery}
          onReason={(r) => { setReason(r); setPage(1); }} suggestions={visibleSuggestions.length}
          onConfirmSuggestions={openConfirmList} />
      )}
      {!data ? <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#0f5ef7]" /></div>
        : data.count === 0 ? (
          <div className="tr-card flex flex-col items-center gap-2 p-10 text-center" data-testid="bandeja-empty">
            <Inbox className="h-8 w-8 text-[#9bc1ff]" /><p className="font-bold text-[#182235]">No hay archivos por clasificar</p>
          </div>
        ) : data.groups.length === 0 ? (
          <div className="tr-card p-8 text-center text-sm text-[#5b667a]" data-testid="bandeja-no-results">No hay carpetas que coincidan con el filtro</div>
        ) : data.groups.map((g, gi) => <PendingGroup key={`${page}-${g.group_path || "root"}`} group={g} gi={gi} onAssignFolder={onAssignFolder}
          onAssignTypeGroup={onAssignTypeGroup} onDiscardGroup={setGroupToDiscard} fileHandlers={fileHandlers} />)}
      {data?.groups.length > 0 && <Pager page={page} pages={pages} total={data.total_groups} onPage={(p) => { setPage(p); window.scrollTo({ top: 0 }); }} />}
      <FileViewer file={viewing} onClose={() => setViewing(null)} basePath="/bandeja" canDownload={isAdmin(user)} />
      <AlertDialog open={!!groupToDiscard} onOpenChange={(o) => !o && setGroupToDiscard(null)}>
        <AlertDialogContent data-testid="discard-group-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Descartar toda la carpeta?</AlertDialogTitle>
            <AlertDialogDescription>Los {groupToDiscard?.files.length} archivos de “{groupToDiscard?.folder}” se quitarán de la bandeja y no se cargarán a ningún expediente.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="discard-group-cancel-button">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDiscardGroup} className="bg-[#b42318] text-white hover:bg-[#912018]" data-testid="discard-group-confirm-button">Descartar carpeta</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!confirmList} onOpenChange={(o) => !o && setConfirmList(null)}>
        <AlertDialogContent data-testid="confirm-suggestions-dialog" className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar {confirmList?.length} {confirmList?.length === 1 ? "sugerencia" : "sugerencias"}</AlertDialogTitle>
            <AlertDialogDescription>Cada carpeta se asignará al cliente sugerido. Revisa la lista antes de confirmar.</AlertDialogDescription>
          </AlertDialogHeader>
          <ul className="max-h-80 space-y-1.5 overflow-y-auto text-sm" data-testid="confirm-suggestions-list">
            {confirmList?.map((g, i) => (
              <li key={g.group_path} className="rounded-lg bg-[#f5f8fd] px-3 py-2" data-testid={`confirm-suggestion-item-${i}`}>
                <span className="font-semibold text-[#182235]">{g.folder}</span> <span className="text-[#5b667a]">→</span> <span className="font-semibold text-[#0f5ef7]">{g.near_client_name}</span>
              </li>
            ))}
          </ul>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-suggestions-cancel-button">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmSuggestions} data-testid="confirm-suggestions-confirm-button">Sí, asignar todas</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!toDiscard} onOpenChange={(o) => !o && setToDiscard(null)}>
        <AlertDialogContent data-testid="discard-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Descartar archivo?</AlertDialogTitle>
            <AlertDialogDescription>“{toDiscard?.filename}” se quitará de la bandeja y no se cargará a ningún expediente.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="discard-cancel-button">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDiscard} className="bg-[#b42318] text-white hover:bg-[#912018]" data-testid="discard-confirm-button">Descartar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default function BandejaPage() {
  const { user } = useAuth();
  return <AppShell>{canWrite(user) ? <Bandeja /> : <NoPermission />}</AppShell>;
}
